import { stateAbbreviations } from '@constants/stateAbbreviations'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import { useEffect, useState } from 'react'

export const siteCreateConfig = (createSelectOptions) => {
  return [
    {
      step_title: 'Name',
      form_title: 'Identify Terminal',
      section: [
        {
          name: 'store_number',
          input: 'input',
          label: 'Site Number',
          placeholder: 'Site Number',
          rules: [
            {
              required: true,
              message: 'Site number is required',
            },
          ],
        },
        {
          name: 'name',
          input: 'input',
          placeholder: 'Enter Site Name',
          rules: [
            {
              required: true,
              message: 'Site name is required.',
            },
          ],
        },
        {
          name: 'counterparty',
          input: 'select',
          placeholder: 'Select Counterparty',
          type: 'single',
          options: createSelectOptions?.counterparties?.map((cp) => cp.name),
          rules: [
            {
              required: true,
              message: 'Counterparty is required',
            },
          ],
        },
        {
          name: 'sector',
          input: 'select',
          type: 'single',
          placeholder: 'Select Sector',
          options: createSelectOptions?.sectors?.flatMap((market) => market.options),
          rules: [
            {
              required: true,
              message: 'Sector is required',
            },
          ],
        },
        {
          name: 'trailer_config',
          input: 'select',
          type: 'single',
          placeholder: 'Select Trailer Config',
          options: createSelectOptions?.trailer_configs,
        },
      ],
    },
    {
      step_title: 'Location',
      form_title: 'Site Location',
      section: [
        {
          name: 'address',
          input: 'input',
          type: 'text',
          placeholder: 'Enter Address',
        },
        {
          name: 'city',
          input: 'input',
          placeholder: 'Enter City',
        },
        {
          name: 'state',
          input: 'select',
          placeholder: 'Select State',
          type: 'single',
          options: stateAbbreviations,
        },
        {
          name: 'lat',
          label: 'Latitude',
          input: 'inputNumber',
          min: -90,
          max: 90,
          precision: 8,
          placeholder: 'Enter Latitude',
          rules: [
            {
              required: true,
              message: 'latitude is required',
            },
          ],
        },
        {
          name: 'lon',
          label: 'Longitude',
          input: 'inputNumber',
          min: -180,
          max: 180,
          precision: 8,
          placeholder: 'Enter Longitude',
          rules: [
            {
              required: true,
              message: 'Longitude is required',
            },
          ],
        },
      ],
    },
    {
      step_title: 'Config',
      form_title: 'Site Configuration',
      section: [
        {
          name: 'phone_number',
          label: 'Contact Number',
          input: 'input',
          type: 'text',
          placeholder: 'Enter Contact Number',
        },
        {
          label: 'Site Hours',
          name: 'hours',
          input: 'input',
          placeholder: 'Enter Site Hours',
        },
        {
          name: 'monitoring_strategy',
          input: 'select',
          type: 'single',
          placeholder: 'Select Monitoring Strategy',
          options: createSelectOptions?.monitoring_strategies,
          rules: [
            {
              required: true,
              message: 'Monitoring Strategy is required',
            },
          ],
        },
        {
          name: 'freight_region_id',
          input: 'select',
          type: 'single',
          label: 'Freight Region',
          placeholder: 'Select Freight Region',
          options: createSelectOptions?.freight_regions?.map((region) => region.name),
          transform: (value) => {
            if (!value) {
              return { freight_region_id: null, freight_region_name: null }
            }

            // Find the region by name
            const region = createSelectOptions?.freight_regions?.find((r) => r.name === value)

            return {
              freight_region_id: region?.id || null,
              freight_region_name: value,
            }
          },
        },
      ],
    },
  ]
}

// Updated hook to fetch and provide freight regions to the siteCreateConfig
export const useCreateConfig = () => {
  const [freightRegions, setFreightRegions] = useState([])
  const { data: metadataOptions, isLoading, isError } = useMasterMetadata('ratebook_options')

  useEffect(() => {
    if (metadataOptions && metadataOptions.freight_regions) {
      // Keep the original structure but ensure we have consistent property names
      const formattedRegions = metadataOptions.freight_regions.map((region) => {
        return {
          id: region.id,
          name: region.name,
          // Add common properties used by different select implementations
          value: region.id,
          label: region.name,
          text: region.name,
        }
      })

      setFreightRegions(formattedRegions)
    }
  }, [metadataOptions, isError])

  // Return a function that will be called by GraviGrid
  const configFunction = (otherOptions = {}) => {
    // Make sure freight_regions is always accessible in createSelectOptions
    const createSelectOptions = {
      ...otherOptions,
      freight_regions: freightRegions,
    }

    return siteCreateConfig(createSelectOptions)
  }

  return {
    isLoading,
    siteCreateConfig: configFunction,
  }
}

// Function to handle freight region display in the grid
export const getRegionNameFromId = (regionId, freightRegions) => {
  if (!regionId || !freightRegions?.length) return ''
  const region = freightRegions.find((r) => r.id === regionId)
  return region?.name || ''
}
